import { motion } from "framer-motion";
import { Calendar as CalendarIcon, BarChart2, CheckCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import Card from "../components/ui/Card";
import { PRAYERS } from "../lib/constants";
import { monthMatrix } from "../lib/dates";
import { useAppState } from "../context/AppStateContext";

function getWeeklyStats(prayerLog) {
  // Returns array of 7 objects: { date, completed, percentage }
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const iso = d.toISOString().split("T")[0];
    const log = prayerLog[iso] || {};
    const completed = PRAYERS.filter((p) => log[p]).length;
    days.push({
      date: iso,
      completed,
      percentage: Math.round((completed / PRAYERS.length) * 100),
      prayers: log,
    });
  }
  return days;
}

export default function TrackPage({ goTo }) {
  const { state, today } = useAppState();
  const [calMonth, setCalMonth] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), monthIndex: d.getMonth() };
  });
  const [expandedDate, setExpandedDate] = useState(null);
  const [showAllDeeds, setShowAllDeeds] = useState(false);

  // Use the global slip/streak wording toggle from settings
  const showAsSlip = state.settings?.showAsSlip ?? true;

  const monthWeeks = useMemo(() => monthMatrix(calMonth.year, calMonth.monthIndex), [calMonth]);
  
  const weeklyStats = useMemo(() => getWeeklyStats(state.prayerLog), [state.prayerLog]);

  // MEMOIZED: Slips this week (critical performance fix)
  const slipsThisWeek = useMemo(() => {
    if (!state.history) return 0;
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    return state.history.filter(entry => new Date(entry.endAt).getTime() > sevenDaysAgo).length;
  }, [state.history]);

  // MEMOIZED: Best streak duration (critical performance fix)
  const bestStreakDays = useMemo(() => {
    if (!state.history || state.history.length === 0) return null;
    const max = Math.max(...state.history.map(e => e.durationMs || 0));
    const days = Math.floor(max / (1000 * 60 * 60 * 24));
    return days > 0 ? `${days}d` : "<1d";
  }, [state.history]);

  // MEMOIZED: Calendar cell data with O(1) slip lookup (critical performance fix)
  const calendarCellData = useMemo(() => {
    // Create a Set of slip dates for O(1) lookup instead of O(n)
    const slipDateSet = new Set(
      state.history
        ?.filter(entry => entry.endAt)
        .map(entry => entry.endAt.split("T")[0])
    );

    // Pre-calculate prayer data for all dates
    return monthWeeks.flat().map((cell) => {
      const iso = cell.iso;
      const p = state.prayerLog[iso] || {};
      const done = PRAYERS.reduce((acc, x) => acc + (p[x] ? 1 : 0), 0);
      const slipped = slipDateSet.has(iso); // ✅ O(1) instead of O(n)
      
      return {
        iso,
        date: cell.date,
        inMonth: cell.inMonth,
        done,
        slipped,
        prayers: p,
      };
    });
  }, [monthWeeks, state.prayerLog, state.history]);

  // Get recent completed deeds (cap at 50 for performance)
  const allRecentDeeds = useMemo(() => {
    const deedLog = state.dailyDeeds?.log || {};
    return Object.entries(deedLog)
      .sort(([, a], [, b]) => new Date(b.completedAt) - new Date(a.completedAt))
      .slice(0, 50)  // Cap at 50 for performance
      .map(([key, deed]) => ({
        key,
        ...deed,
      }));
  }, [state.dailyDeeds]);

  const recentDeeds = useMemo(() => {
    return showAllDeeds ? allRecentDeeds : allRecentDeeds.slice(0, 5);  // Show 5 by default instead of 3
  }, [allRecentDeeds, showAllDeeds]);

  // Memoized month navigation handlers
  const handlePrevMonth = useCallback(() => {
    const m = calMonth.monthIndex - 1;
    if (m < 0) setCalMonth({ year: calMonth.year - 1, monthIndex: 11 });
    else setCalMonth({ ...calMonth, monthIndex: m });
  }, [calMonth]);

  const handleNextMonth = useCallback(() => {
    const m = calMonth.monthIndex + 1;
    if (m > 11) setCalMonth({ year: calMonth.year + 1, monthIndex: 0 });
    else setCalMonth({ ...calMonth, monthIndex: m });
  }, [calMonth]);

  const handleTodayClick = useCallback(() => {
    const d = new Date();
    setCalMonth({ year: d.getFullYear(), monthIndex: d.getMonth() });
    setExpandedDate(d.toISOString().split("T")[0]);
  }, []);

  const handleToggleDeedsView = useCallback(() => {
    setShowAllDeeds(prev => !prev);
  }, []);

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-2 flex flex-col" style={{ paddingBottom: "calc(80px + env(safe-area-inset-bottom, 20px))" }}>
      <Card
        title="This Week"
        icon={BarChart2}
        right={
          <button
            onClick={() => goTo && goTo("analytics")}
            className="px-4 py-2 rounded-xl bg-[#10B981] text-zinc-900 font-semibold text-sm hover:bg-emerald-600 transition-all active:scale-95 min-h-[44px]"
          >
            View Detailed Insights
          </button>
        }
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Sparkline */}
          <div className="flex-1">
            <div className="mb-2 text-xs text-zinc-500 dark:text-zinc-400 font-medium">Prayer completion this week</div>
            <div className="flex items-end justify-between gap-1.5 h-16">
              {weeklyStats.map((day, idx) => {
                const dayOfWeek = ["S", "M", "T", "W", "T", "F", "S"][new Date(day.date).getDay()];
                const isToday = day.date === today;
                return (
                  <div key={`${day.date}-${idx}`} className="flex-1 flex flex-col items-center gap-1">
                    <div
                      className={`w-full rounded-t-sm transition-all ${
                        day.completed === PRAYERS.length
                          ? "bg-gradient-to-t from-green-500 to-emerald-400"
                          : day.completed > 0
                          ? "bg-gradient-to-t from-amber-500 to-orange-400"
                          : "bg-zinc-300 dark:bg-zinc-700"
                      } ${isToday ? "ring-2 ring-offset-1 ring-zinc-400 dark:ring-zinc-500" : ""}`}
                      style={{
                        height: `${Math.max(day.percentage, 15)}%`,
                        minHeight: "8px"
                      }}
                      title={`${day.completed}/${PRAYERS.length} prayers`}
                    />
                    <div className="text-[10px] font-semibold text-zinc-600 dark:text-zinc-400">{dayOfWeek}</div>
                    <div className="text-[9px] text-zinc-500 dark:text-zinc-500">{day.completed}/5</div>
                  </div>
                );
              })}
            </div>
          </div>
          {/* Slip/Streak Data - horizontal layout */}
          <div className="flex flex-row items-end justify-between gap-8 min-w-[220px] w-full md:w-auto">
            {/* Slips this week */}
            <div>
              <div className="text-xs text-zinc-500 dark:text-zinc-400 font-medium">
                {showAsSlip ? "Slips this week" : "Streaks ended this week"}
              </div>
              <div className="text-3xl font-bold text-zinc-900 dark:text-white">
                {slipsThisWeek}
              </div>
            </div>
            {/* Best streak */}
            <div className="text-right">
              <div className="text-xs text-zinc-500 dark:text-zinc-400 font-medium">
                Best streak
              </div>
              <div className="text-lg font-semibold text-emerald-600 dark:text-emerald-400">
                {bestStreakDays ?? "—"}
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* --- Calendar Tracking --- */}
      <Card className="p-4">
        {/* Centered Title */}
        <div className="flex items-center justify-center gap-2 mb-4">
          <CalendarIcon size={20} className="text-[#10B981]" />
          <h2 className="font-bold text-white">Calendar tracking</h2>
        </div>

        {/* Navigation Controls - Centered Below Title */}
        <div className="flex items-center justify-center gap-3 mb-4">
          <button
            onClick={handlePrevMonth}
            aria-label="Previous month"
            className="p-px rounded-md bg-zinc-900 hover:bg-zinc-800 ring-1 ring-zinc-700 hover:ring-zinc-600 transition-all active:scale-95"
            title="Previous month"
          >
            <ChevronLeft className="h-5 w-5 text-white" />
          </button>
          <div className="text-sm font-semibold text-center min-w-[80px]">
            {new Date(calMonth.year, calMonth.monthIndex).toLocaleString(undefined, { month: "long", year: "numeric" })}
          </div>
          <button
            onClick={handleNextMonth}
            aria-label="Next month"
            className="p-px rounded-md bg-zinc-900 hover:bg-zinc-800 ring-1 ring-zinc-700 hover:ring-zinc-600 transition-all active:scale-95"
            title="Next month"
          >
            <ChevronRight className="h-5 w-5 text-white" />
          </button>
          <button
            onClick={handleTodayClick}
            aria-label="Jump to today"
            className="px-4 py-2 rounded-lg bg-[#10B981] text-zinc-900 text-xs font-bold hover:bg-emerald-600 active:scale-95 transition-all ring-1 ring-emerald-600/50 hover:ring-emerald-500/50 min-h-[36px] flex items-center justify-center whitespace-nowrap"
            title="Jump to today"
          >
            Today
          </button>
        </div>

        {/* --- Day-of-week header --- */}
        <div className="grid grid-cols-7 gap-1 text-xs text-zinc-500 dark:text-zinc-400 mb-2">
          {["S", "M", "T", "W", "T", "F", "S"].map((d, idx) => (
            <div key={`day-${idx}`} className="text-center">
              {d}
            </div>
          ))}
        </div>

        {/* --- Calendar grid --- */}
        <div className="grid grid-cols-7 gap-1">
          {calendarCellData.map((cellData) => {
            const isToday = cellData.iso === today;

            return (
              <button
                key={cellData.iso}
                onClick={() => setExpandedDate(expandedDate === cellData.iso ? null : cellData.iso)}
                aria-label={`${cellData.date.toLocaleDateString()} - ${cellData.done} of 5 prayers${cellData.slipped ? " (slip)": ""}`}
                className={
                  "aspect-square rounded-xl ring-1 text-sm flex flex-col items-center justify-center relative overflow-hidden transition-all " +
                  (cellData.inMonth
                    ? isToday
                      ? "bg-zinc-900 ring-[#10B981] glow-mint"
                      : "bg-zinc-900/50 ring-zinc-800 hover:ring-zinc-700"
                    : "bg-zinc-950/30 ring-zinc-900/50")
                }
                title="Click to view details"
              >
                <div className={"font-semibold " + (cellData.inMonth ? "text-white" : "text-zinc-600")}>{cellData.date.getDate()}</div>
                <div className="text-[10px] text-zinc-600">{cellData.done}/5</div>
                {cellData.slipped ? <div className="absolute inset-x-0 bottom-0 h-1 bg-[#10B981]" /> : null}
                {isToday ? <div className="absolute inset-0 ring-2 ring-[#10B981] rounded-xl glow-mint" /> : null}
              </button>
            );
          })}
        </div>

        <div className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">
          Tap a day to view details. The bottom bar marks a day with a slip/streak end.
        </div>

        {/* --- Daily Expansion: Prayer details, notes, slip reasons --- */}
        {expandedDate && (
          <div className="mt-4 space-y-4">
            {/* --- Prayer breakdown --- */}
            <div>
              <div className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase mb-2">
                Prayers completed
              </div>
              <div className="flex gap-2">
                {PRAYERS.map((p) => (
                  <div
                    key={p}
                    className={
                      "px-2 py-1 rounded-lg text-xs font-semibold " +
                      (state.prayerLog[expandedDate]?.[p]
                        ? "bg-green-200 dark:bg-green-700 text-green-900 dark:text-green-100"
                        : "bg-red-200 dark:bg-red-800 text-red-600 dark:text-red-300")
                    }
                  >
                    {p}
                  </div>
                ))}
              </div>
            </div>

            {/* --- Notes --- */}
            <div>
              <div className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase mb-2">
                Reflection / Notes
              </div>
              <div className="text-sm text-zinc-700 dark:text-zinc-300 p-2 rounded-lg bg-white/50 dark:bg-zinc-900/50">
                {state.notes[expandedDate] || <span className="italic text-zinc-400">No notes for this day.</span>}
              </div>
            </div>

            {/* --- Slip/Streak Details --- */}
            {state.history?.filter((entry) => entry.endAt && entry.endAt.split("T")[0] === expandedDate).length === 0 ? (
              <div className="text-xs text-zinc-400 text-center py-2">No slip/streak end recorded for this day.</div>
            ) : (
              state.history
                ?.filter((entry) => entry.endAt && entry.endAt.split("T")[0] === expandedDate)
                .map((entry, idx) => (
                  <div key={`${entry.startAt}-${entry.endAt}-${idx}`} className="border-t border-zinc-300 dark:border-zinc-700 pt-3 mt-2">
                    <div className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase mb-2">
                      {showAsSlip ? "Slip Details" : "Streak Details"}
                    </div>
                    {entry.slipReasons && entry.slipReasons.length > 0 ? (
                      <div className="mb-3">
                        <div className="text-xs text-zinc-600 dark:text-zinc-400 mb-1">
                          {showAsSlip ? "What led to the slip:" : "What led to the streak ending:"}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {entry.slipReasons.map((reason, i) => (
                            <div
                              key={reason + i}
                              className="px-2 py-1 rounded-lg bg-zinc-300 dark:bg-zinc-700 text-xs text-zinc-700 dark:text-zinc-200"
                            >
                              {reason}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-xs text-zinc-500 dark:text-zinc-400 mb-3 italic">
                        {showAsSlip ? "No reasons recorded" : "No reasons recorded"}
                      </div>
                    )}

                    {entry.slipNotes ? (
                      <div className="mb-3">
                        <div className="text-xs text-zinc-600 dark:text-zinc-400 mb-1">
                          {showAsSlip ? "Additional details:" : "Additional details:"}
                        </div>
                        <div className="text-sm text-zinc-700 dark:text-zinc-300 p-2 rounded-lg bg-white/50 dark:bg-zinc-900/50">
                          {entry.slipNotes}
                        </div>
                      </div>
                    ) : null}

                    <div className="text-xs text-zinc-600 dark:text-zinc-400">
                      <div className="mb-2">
                        <span className="font-semibold">
                          {showAsSlip ? "Slip timestamp:" : "Streak ended:"}
                        </span>{" "}
                        {new Date(entry.endAt).toLocaleString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                        })}
                      </div>
                      <div>
                        <span className="font-semibold">
                          {showAsSlip ? "Streak duration:" : "Streak lasted:"}
                        </span>{" "}
                        {Math.floor(entry.durationMs / (1000 * 60 * 60 * 24))} days,{" "}
                        {Math.floor((entry.durationMs / (1000 * 60 * 60)) % 24)} hours,{" "}
                        {Math.floor((entry.durationMs / (1000 * 60)) % 60)} minutes
                      </div>
                    </div>
                  </div>
                ))
            )}
          </div>
        )}
      </Card>
      {/* --- Recently Completed Deeds --- */}
      {recentDeeds.length > 0 && (
        <Card title="Recently Completed Deeds" icon={CheckCircle}>
          <div className="space-y-2">
            {recentDeeds.map((deed, idx) => {
              const deedDate = new Date(deed.completedAt);
              const displayDate = deedDate.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: deedDate.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined,
              });
              const displayTime = deedDate.toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              });

              // Get icon based on deed type
              const getTypeEmoji = (type) => {
                switch (type) {
                  case "audio": return "🎵";
                  case "prayer": return "🤲";
                  case "reflection": return "📝";
                  case "asmaul_husna": return "📿";
                  default: return "✨";
                }
              };

              return (
                <div key={deed.key} className="flex items-start justify-between p-3 rounded-lg bg-emerald-950/20 border border-emerald-900/30">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{getTypeEmoji(deed.type)}</span>
                      <div className="text-sm font-semibold text-white">{deed.deedTitle}</div>
                    </div>
                    <div className="text-xs text-zinc-500 mt-1 ml-6">{displayDate} at {displayTime}</div>
                  </div>
                  <CheckCircle size={20} className="text-[#10B981] flex-shrink-0 glow-mint" />
                </div>
              );
            })}
            {allRecentDeeds.length > 5 && (
              <button
                onClick={handleToggleDeedsView}
                aria-label={showAllDeeds ? "Show fewer deeds" : `Show more deeds (${allRecentDeeds.length - 5} more)`}
                className="w-full mt-3 px-3 py-2 text-xs font-semibold text-[#10B981] hover:text-[#10B981]/80 transition-colors"
              >
                {showAllDeeds ? "Show Less" : `Show More (${allRecentDeeds.length - 5} more)`}
              </button>
            )}
          </div>
        </Card>
      )}
    </motion.div>
  );
}
